import { useEffect, useMemo, useRef, useState } from 'react'

import { normalizeCommunityRecords } from './communityUtils'
import { DASHBOARD_POSTS_PAGE_SIZE } from './communityConstants'

// Turns a live "newest N posts" listener into a list with a Load more action.
// Used by the feed (ordered by createdAt) and the "Your posts" tab (ordered by
// key, since every post there shares one authorId).
//
// Two listeners make up the list:
// - The head, owned by the caller: always the newest `pageSize` posts.
// - The older window, attached through `useOlderQuery` once Load more has been
//   pressed: the `olderLimit` posts at or before the head's oldest post (the
//   `cursor`). Each press grows olderLimit by a page, so it is one live window
//   that widens downward — edits, pins and deletes on older posts stay live,
//   and nothing on the page is ever a stale copy.
//
// When a new post arrives the head slides and its oldest post drops out. The
// cursor follows the head, which moves the older window's top edge up; to keep
// its bottom edge where it was, olderLimit grows by the number of posts that
// dropped out. Without that, posts would vanish from the seam between the two
// listeners. A post missing from the new head but still inside its range was
// deleted, not evicted (limitToLast refills a full window from below), and is
// not counted.
//
// `useOlderQuery({ cursor, limit, skip })` must call exactly one RTK Query hook
// and return its result; it is called once per render, unconditionally, so the
// rules of hooks hold.
export default function usePostPager({ liveData, getOrderValue, useOlderQuery, pageSize = DASHBOARD_POSTS_PAGE_SIZE }) {
  const liveLoading = liveData === undefined || liveData?.isLoading
  const livePosts = useMemo(() => (liveLoading ? {} : normalizeCommunityRecords(liveData)), [liveData, liveLoading])

  const [pagesRequested, setPagesRequested] = useState(0)
  const [evictedCount, setEvictedCount] = useState(0)
  const prevLiveRef = useRef(null)

  useEffect(() => {
    if (liveLoading) return
    const prev = prevLiveRef.current
    prevLiveRef.current = livePosts
    // Before the first Load more the cursor has not been used yet, so anything
    // that slid out simply becomes "older" and the first page picks it up.
    if (!prev || pagesRequested === 0) return

    const liveIds = new Set(Object.keys(livePosts))
    if (liveIds.size < pageSize) return
    const oldestLiveValue = minOrderValue(livePosts, getOrderValue)
    const evicted = Object.entries(prev).filter(
      ([id, post]) => !liveIds.has(id) && getOrderValue(post, id) < oldestLiveValue
    ).length
    if (evicted > 0) setEvictedCount((count) => count + evicted)
  }, [livePosts, liveLoading, pagesRequested, pageSize, getOrderValue])

  const cursor = useMemo(() => {
    let oldest = null
    Object.entries(livePosts).forEach(([id, post]) => {
      const value = getOrderValue(post, id)
      if (value === undefined || value === null) return
      if (!oldest || value < oldest.value) oldest = { id, value }
    })
    return oldest
  }, [livePosts, getOrderValue])

  const olderLimit = pagesRequested * pageSize + evictedCount
  const olderActive = pagesRequested > 0 && cursor !== null
  const { data: olderData } = useOlderQuery({ cursor, limit: olderLimit, skip: !olderActive })
  const olderLoading = olderActive && Boolean(olderData === undefined || olderData?.isLoading)

  // While the older window re-attaches (a new page, or the cursor moved with
  // the head) keep showing the last window rather than blanking the list.
  const lastOlderRef = useRef({})
  const olderPosts = useMemo(() => {
    if (!olderActive) return {}
    if (!olderLoading) lastOlderRef.current = normalizeCommunityRecords(olderData)
    return lastOlderRef.current
  }, [olderActive, olderLoading, olderData])

  // A full window means Firebase may have more below it.
  const hasMore = olderActive
    ? olderLoading || Object.keys(olderPosts).length >= olderLimit
    : Object.keys(livePosts).length >= pageSize

  // Live head wins on the boundary post the older window also holds.
  const posts = useMemo(() => ({ ...olderPosts, ...livePosts }), [olderPosts, livePosts])

  return {
    posts,
    isLoading: liveLoading,
    hasMore,
    isLoadingMore: olderLoading,
    loadMore: () => setPagesRequested((count) => count + 1),
  }
}

const minOrderValue = (posts, getOrderValue) => {
  let min = null
  Object.entries(posts).forEach(([id, post]) => {
    const value = getOrderValue(post, id)
    if (value === undefined || value === null) return
    if (min === null || value < min) min = value
  })
  return min
}
